<template>
  <div class="flex flex-col w-96">
    <NText variant="caption1" weight="bold" class="block pr-3 mb-2">
      {{ t('symbol.broker_change_request') }}
    </NText>

    <div class="bg-neutral-secondary p-4 rounded-md">
      <NText
        variant="caption1"
        weight="medium"
        class="text-neutral-secondary-press block pr-3 mb-2"
      >
        {{ t('modules.watchlist.symbol_name') }}
      </NText>

      <SymbolSearch class="w-[22rem]" @update-symbol="updateSymbol" />

      <NCheap
        v-if="symbol.instrumentName"
        :label="symbol.instrumentName"
        color="green"
        closable
        @close="removeSymbol"
      />

      <NButton
        v-gid="{
          module: 'symbol',
          screen: 'home',
          section: 'brokerChangeDialog',
          component: 'submitButton',
          disabled: !Boolean(symbol?.isin) || isUpdatingSymbolBrokerRequest,
        }"
        variant="fill"
        color="green"
        class="w-full mt-6"
        :disabled="!Boolean(symbol?.isin) || isUpdatingSymbolBrokerRequest"
        :loading="isUpdatingSymbolBrokerRequest"
        @click="updateSymbolBrokerChangeRequest(symbol?.isin)"
      >
        {{ t('common.submit_request') }}
      </NButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from '@negar/core';
import {
  SYMBOL_QUERY_KEYS,
  useUpdateSymbolBrokerRequestQuery,
} from '@core/symbol';
import { useQueryClient } from '@libs/use';

import { SymbolSearch } from '@/modules/symbol';

const { t } = useI18n();

const toast = useToast();

const symbol = reactive({
  isin: null,
  instrumentName: null,
});

const removeSymbol = () => {
  Object.assign(symbol, {
    isin: null,
    instrumentName: null,
  });
};

const queryClient = useQueryClient();

const { updateSymbolBrokerChangeRequest, isUpdatingSymbolBrokerRequest } =
  useUpdateSymbolBrokerRequestQuery({
    onSuccess: () => {
      queryClient.invalidateQueries(SYMBOL_QUERY_KEYS.brokerChange);

      toast.positive({
        message: t('symbol.broker_change_success_message', {
          instrumentName: symbol.instrumentName,
        }),
        duration: 3000,
      });

      Object.assign(symbol, {
        isin: null,
        instrumentName: null,
      });
    },
    onReject(error) {
      if (error) {
        for (const { message } of error.response.data) {
          toast.negative({
            message,
            duration: 3000,
          });
        }
      }
    },
  });

const updateSymbol = ({
  isin,
  name,
}: {
  isin: string | null;
  name: string | null;
}) => {
  Object.assign(symbol, {
    isin,
    instrumentName: name,
  });
};
</script>
